import { useFiltersStore } from "@/stores/filtersStore";
import { formatPriceWithSymbol } from "@/lib/shared/currency";
import styles from "./styles.module.scss";
import { Text as MantineText } from "@mantine/core";

interface PriceChangeProps {
  oldPriceUsd?: number | null;
  newPriceUsd?: number | null;
  changeTimestamp?: string | null;
}

interface Props {
  priceChange?: PriceChangeProps | null;
  isSold: boolean;
  displayedPrice: number; // The actual price shown to user (already converted to their currency)
  className?: string;
}

const THRESHOLD_PERCENT = 10;

export default function PriceBadge({ priceChange, isSold, displayedPrice, className }: Props) {
  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const selectedCurrency = (activeFilters.currency?.[0]?.value as string) || "USD";

  if (
    isSold ||
    !priceChange?.oldPriceUsd ||
    !priceChange?.newPriceUsd ||
    !displayedPrice ||
    isNaN(displayedPrice)
  ) {
    return null;
  }

  if (priceChange.newPriceUsd < priceChange.oldPriceUsd) {
    const dropPercent =
      ((priceChange.oldPriceUsd - priceChange.newPriceUsd) / priceChange.oldPriceUsd) * 100;

    if (dropPercent >= THRESHOLD_PERCENT) {
      // Reverse-calculate old price from displayed price using the same percentage drop
      // This ensures the badge matches what the user sees
      const oldPrice = displayedPrice / (1 - dropPercent / 100);
      const oldPriceText = formatPriceWithSymbol({
        price: oldPrice,
        currencyCode: selectedCurrency,
        abbreviate: true,
        roundToFive: false,
      });

      return (
        <MantineText span className={`${styles.dropBadge}${className ? ` ${className}` : ""}`}>
          {oldPriceText}
        </MantineText>
      );
    }
  }

  return null;
}
